import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Chip } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { AccountLedger as IAccountLedger } from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

const tableCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
};

const formatAmount = (value?: number) =>
    value != null ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
const todayStr = today.toISOString().split('T')[0];

const AccountLedger: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [ledger, setLedger] = useState<IAccountLedger | null>(null);
    const [loading, setLoading] = useState(false);
    const [from, setFrom] = useState(firstOfMonth);
    const [to, setTo] = useState(todayStr);

    const fetchLedger = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await AccountService.getAccountLedger(id, from, to);
            setLedger(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load ledger');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLedger();
    }, [id]);

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/accounting/chart-of-accounts')}>
                    Chart of Accounts
                </Button>
                <h2 style={{ margin: 0 }}>
                    Account Ledger
                    {ledger && (
                        <span style={{ marginLeft: '12px', fontSize: '16px', color: '#555', fontWeight: 400 }}>
                            {ledger.accountCode} — {ledger.accountName}
                            <Chip label={ledger.accountType} size="small" sx={{ ml: 1 }} />
                        </span>
                    )}
                </h2>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: 500 }}>
                    From:&nbsp;
                    <input
                        type="date"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <label style={{ fontWeight: 500 }}>
                    To:&nbsp;
                    <input
                        type="date"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <Button variant="contained" onClick={fetchLedger} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
            </div>

            {ledger && (
                <div style={{ marginBottom: '12px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <span><strong>Total Debits:</strong> {formatAmount(ledger.totalDebits)}</span>
                    <span><strong>Total Credits:</strong> {formatAmount(ledger.totalCredits)}</span>
                    <span><strong>Closing Balance:</strong> {formatAmount(ledger.closingBalance)}</span>
                </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Date</th>
                        <th style={tableHeaderStyle}>Entry #</th>
                        <th style={tableHeaderStyle}>Description</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Debit</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Credit</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={6} style={{ ...tableCellStyle, textAlign: 'center' }}>Loading...</td></tr>
                    )}
                    {!loading && (!ledger || ledger.lines.length === 0) && (
                        <tr>
                            <td colSpan={6} style={{ ...tableCellStyle, textAlign: 'center', color: '#888' }}>
                                No posted entries for this account in the selected period.
                            </td>
                        </tr>
                    )}
                    {!loading && ledger && ledger.lines.length > 0 && (
                        <>
                            <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 600 }}>
                                <td style={tableCellStyle}>{ledger.fromDate}</td>
                                <td style={tableCellStyle}>—</td>
                                <td style={tableCellStyle}>Opening Balance</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}></td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}></td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(ledger.openingBalance)}</td>
                            </tr>
                            {ledger.lines.map((line, i) => (
                                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={tableCellStyle}>{line.date}</td>
                                    <td style={tableCellStyle}>{line.entryNumber}</td>
                                    <td style={tableCellStyle}>{line.lineDescription || line.entryDescription || '—'}</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.debit)}</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.credit)}</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 500 }}>{formatAmount(line.runningBalance)}</td>
                                </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 700 }}>
                                <td style={tableCellStyle}>{ledger.toDate}</td>
                                <td style={tableCellStyle}></td>
                                <td style={tableCellStyle}>Closing Balance</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(ledger.totalDebits)}</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(ledger.totalCredits)}</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(ledger.closingBalance)}</td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default AccountLedger;
